import {
	SainAction,
	useSAINUsageCriteriaMet,
} from '../../common/controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../common/controller/analytics/use-analytics';
import { FEEDBACK_ENTRYPOINT_ID_SAIN } from '../../constants';
import { useAIAnswerContext } from '../ai-answer-context';
import { onDeselectDislikeButtonClick } from '../on-deselect-dislike-button-click';
import { onDeselectLikeButtonClick } from '../on-deselect-like-button-click';
import { onDislikeButtonClick } from '../on-dislike-button-click';
import { onFeedbackSubmit } from '../on-feedback-submit';
import { onLikeButtonClick } from '../on-like-button-click';
import type { FeedbackSubmitHandlerProps, NLPSearchErrorState } from '../types';

export function useSmartAnswerCardFeedbackOptions(): {
	attributes: {
		searchSessionId: string;
		source: string;
		queryHash: string;
		cloudId: string;
		documentId: string | undefined;
		errorState: NLPSearchErrorState | null;
	};
	attributesWithConsent: {
		transformedPrompt: string;
		originalPrompt: any;
		answer: string;
	};
	entrypointId: string;
	onLikeButtonClickHandler: () => void;
	onDislikeButtonClickHandler: () => void;
	onDeselectLikeButtonClickHandler: () => void;
	onDeselectDislikeButtonClickHandler: () => void;
	onFeedbackSubmitHandler: ({
		liked,
		feedbackOptions,
		additionalFeedbackAdded,
		includeResponse,
		canContact,
	}: FeedbackSubmitHandlerProps) => void;
	userDetails: {
		id?: string;
		name?: string;
	};
} {
	const { commonAttributes, readingAidsOptions, updateContextCache, userDetails } =
		useAIAnswerContext();
	const {
		query,
		errorState,
		searchSessionId,
		source,
		queryHash,
		cloudId,
		answerString,
		extraAttributes,
	} = commonAttributes;
	const { originalQuery } = extraAttributes;

	const [fireAnalyticsEvent] = useAnalytics();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();

	const onLikeButtonClickHandler = () => {
		updateContextCache({ collapsed: false, liked: true });
		fireAnalyticsEvent(onLikeButtonClick(commonAttributes));
		fireSAINUsageEvent({
			metCriteria: SainAction.POSITIVE_FEEDBACK_PROVIDED,
			commonAttributes,
		});
	};

	const onDislikeButtonClickHandler = () => {
		updateContextCache({ collapsed: false, liked: false });
		fireAnalyticsEvent(onDislikeButtonClick(commonAttributes));
		fireSAINUsageEvent({
			metCriteria: SainAction.NEGATIVE_FEEDBACK_PROVIDED,
			commonAttributes,
		});
	};

	const onDeselectLikeButtonClickHandler = () => {
		updateContextCache({ collapsed: false, liked: undefined });
		fireAnalyticsEvent(onDeselectLikeButtonClick(commonAttributes));
	};

	const onDeselectDislikeButtonClickHandler = () => {
		updateContextCache({ collapsed: false, liked: undefined });
		fireAnalyticsEvent(onDeselectDislikeButtonClick(commonAttributes));
	};

	const onFeedbackSubmitHandler = ({
		liked,
		feedbackOptions,
		additionalFeedbackAdded,
		includeResponse,
		canContact,
	}: FeedbackSubmitHandlerProps) => {
		fireAnalyticsEvent(
			onFeedbackSubmit({
				...commonAttributes,
				feedbackSentiment: liked ? 'positive' : 'negative',
				feedbackOptions,
				additionalFeedbackAdded,
				includeResponse,
				canContact,
			}),
		);
	};

	const feedbackAttributes = {
		searchSessionId: searchSessionId,
		source: source,
		queryHash: queryHash,
		cloudId: cloudId,
		documentId: readingAidsOptions?.contentARI,
		errorState: errorState,
	};
	const feedbackAttributesWithConsent = {
		transformedPrompt: query,
		originalPrompt: originalQuery,
		answer: answerString,
	};
	const feedbackOptions = {
		attributes: feedbackAttributes,
		attributesWithConsent: feedbackAttributesWithConsent,
		entrypointId: FEEDBACK_ENTRYPOINT_ID_SAIN,
		onLikeButtonClickHandler: onLikeButtonClickHandler,
		onDislikeButtonClickHandler: onDislikeButtonClickHandler,
		onDeselectLikeButtonClickHandler: onDeselectLikeButtonClickHandler,
		onDeselectDislikeButtonClickHandler: onDeselectDislikeButtonClickHandler,
		onFeedbackSubmitHandler: onFeedbackSubmitHandler,
		userDetails,
	};

	return feedbackOptions;
}
